import { useState, useEffect, useMemo } from 'react';
import { FormInstance } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import { useIsOnlineContext } from '../../contexts/IsOnlineContext';
import { locationRepository } from '../../api/repositories/locationRepository';
import { establishmentRepository } from '../../api/repositories/establishmentRepository';
import { referenceDataSyncService } from '../../services/referenceDataSyncService';
import { getTypeOfEstablishmentOptions } from '../../utils/typeOfEstablishmentOptions';

interface UseCollectionSessionFormProps {
  form: FormInstance;
  triggeredFromTask: boolean;
  taskData: any;
}

export function useCollectionSessionForm({
  form,
  triggeredFromTask,
  taskData,
}: UseCollectionSessionFormProps) {
  const { t } = useTranslation(['price', 'common']);
  const { isOnline } = useIsOnlineContext();

  const [allLocations, setAllLocations] = useState<any[]>([]);
  const [allEstablishments, setAllEstablishments] = useState<any[]>([]);

  const [departmentsOptions, setDepartmentsOptions] = useState<any[]>([]);
  const [municipalityOptions, setMunicipalityOptions] = useState<any[]>([]);
  const [establishmentOptions, setEstablishmentOptions] = useState<any[]>([]);
  const [typeOfPositionOptions, setTypeOfPositionOptions] = useState<any[]>([]);

  const [showEstablishmentTextField, setShowEstablishmentTextField] = useState<boolean>(false);
  const [continueButtonClicked, setContinueButtonClicked] = useState<boolean>(false);
  const [loadingLocations, setLoadingLocations] = useState<boolean>(true);
  const [loadingEstablishments, setLoadingEstablishments] = useState<boolean>(true);

  // Load initial reference data
  useEffect(() => {
    let active = true;
    let locationsLoading = true;
    let establishmentsLoading = true;
    let locationsIntervalId: any = null;
    let establishmentsIntervalId: any = null;

    const checkLocationsLoadingState = (locsCount: number) => {
      if (!locationsLoading) {
        if (isOnline && locsCount === 0) {
          setLoadingLocations(true);
        } else {
          setLoadingLocations(false);
        }
      }
    };

    const checkEstablishmentsLoadingState = (estsCount: number) => {
      if (!establishmentsLoading) {
        if (isOnline && estsCount === 0) {
          setLoadingEstablishments(true);
        } else {
          setLoadingEstablishments(false);
        }
      }
    };

    const loadLocationsData = async () => {
      try {
        let loadedLocsCount = 0;
        await locationRepository.loadLocations(
          false, // Retrieve from IndexedDB only
          (updatedLocs) => {
            if (active) {
              setAllLocations(updatedLocs);
              const depts = locationRepository.getUniqueDepartments(updatedLocs);
              setDepartmentsOptions(depts);
              loadedLocsCount = updatedLocs.length;
            }
          },
          () => {
            if (active) {
              locationsLoading = false;
              checkLocationsLoadingState(loadedLocsCount);
            }
          }
        );
      } catch (error) {
        if (active) {
          setLoadingLocations(false);
        }
      }
    };

    const loadEstablishmentsData = async () => {
      try {
        let loadedEstsCount = 0;
        await establishmentRepository.loadEstablishments(
          false, // Retrieve from IndexedDB only
          (updatedEsts) => {
            if (active) {
              setAllEstablishments(updatedEsts);
              loadedEstsCount = updatedEsts.length;
            }
          },
          () => {
            if (active) {
              establishmentsLoading = false;
              checkEstablishmentsLoadingState(loadedEstsCount);
            }
          }
        );
      } catch (error) {
        if (active) {
          setLoadingEstablishments(false);
        }
      }
    };

    setLoadingLocations(true);
    setLoadingEstablishments(true);

    const initData = async () => {
      loadLocationsData();
      loadEstablishmentsData();
    };

    initData();

    if (isOnline) {
      locationsIntervalId = setInterval(async () => {
        if (!active) return;
        const localLocations = await locationRepository.loadLocations(false);
        if (localLocations.length > 0) {
          await loadLocationsData();
          clearInterval(locationsIntervalId);
        }
      }, 2000);

      establishmentsIntervalId = setInterval(async () => {
        if (!active) return;
        const localEstablishments = await establishmentRepository.loadEstablishments(false);
        if (localEstablishments.length > 0) {
          await loadEstablishmentsData();
          clearInterval(establishmentsIntervalId);
        }
      }, 2000);
    }

    return () => {
      active = false;
      if (locationsIntervalId) clearInterval(locationsIntervalId);
      if (establishmentsIntervalId) clearInterval(establishmentsIntervalId);
    };
  }, [isOnline]);

  // Load task details if triggered from task
  useEffect(() => {
    if (triggeredFromTask && taskData) {
      const loadTaskDetails = async () => {
        try {
          if (taskData.locationId) {
            const locationData = await referenceDataSyncService.getLocationById(
              taskData.locationId
            );
            if (locationData) {
              form.setFieldsValue({
                department: locationData.district,
                municipality: locationData.city,
              });
            }
          }
          if (taskData.establishmentId) {
            const establishmentData = await referenceDataSyncService.getEstablishmentDataById(
              taskData.establishmentId
            );
            const est = Array.isArray(establishmentData) ? establishmentData[0] : establishmentData;
            if (est) {
              form.setFieldsValue({
                typeOfPosition:
                  est.establishmentType?.key ||
                  est.establishmentType?.nameEn ||
                  est.establishmentType,
                establishment: est.name,
              });
            }
          }
        } catch (error) {
          console.error('Error loading task details:', error);
        }
      };
      loadTaskDetails();
    }
  }, [triggeredFromTask, taskData, form]);

  const departmentVal = useWatch('department', form);
  const municipalityVal = useWatch('municipality', form);
  const typeOfPositionVal = useWatch('typeOfPosition', form);
  const establishmentVal = useWatch('establishment', form);

  // Filter municipalities when department changes
  useEffect(() => {
    const updateMunicipalities = () => {
      // Query the live value from the form store to protect against undefined from useWatch
      const actualDepartmentVal = form.getFieldValue('department');

      if (!actualDepartmentVal) {
        setMunicipalityOptions([]);
        form.setFieldValue('municipality', undefined);
        return;
      }

      const filtered = locationRepository.filterByDepartment(allLocations, actualDepartmentVal);
      const uniqueMunicipalities = filtered
        .map((loc) => ({
          label: loc.city,
          value: loc.city,
          id: loc.id,
        }))
        .filter((v, i, a) => a.findIndex((t) => t.value === v.value) === i);

      setMunicipalityOptions(uniqueMunicipalities);

      const currentMunicipality = form.getFieldValue('municipality');
      if (currentMunicipality) {
        const isValid = uniqueMunicipalities.some(
          (m) => String(m.value).toLowerCase() === String(currentMunicipality).toLowerCase()
        );
        if (!isValid) {
          form.setFieldValue('municipality', undefined);
        }
      }
    };

    if (allLocations.length > 0) {
      updateMunicipalities();
    } else {
      setMunicipalityOptions([]);
    }
  }, [departmentVal, allLocations, form]);

  // Filter establishment types when department or municipality changes
  useEffect(() => {
    const updateTypeOptions = () => {
      const actualDepartmentVal = form.getFieldValue('department');
      const actualMunicipalityVal = form.getFieldValue('municipality');

      const filteredEstablishments = allEstablishments.filter((est: any) => {
        const location = allLocations.find((loc) => Number(loc.id) === Number(est.locationId));
        const estDept = location?.district || est.department || est.location?.district;
        const estMun = location?.city || est.municipality || est.location?.city;

        let matches = true;

        if (actualDepartmentVal) {
          matches =
            matches && String(estDept).toLowerCase() === String(actualDepartmentVal).toLowerCase();
        }

        if (actualMunicipalityVal) {
          matches =
            matches && String(estMun).toLowerCase() === String(actualMunicipalityVal).toLowerCase();
        }

        return matches;
      });

      const options = establishmentRepository.getUniquePositionTypes(filteredEstablishments, t);

      setTypeOfPositionOptions(options);

      const currentTypeOfPosition = form.getFieldValue('typeOfPosition');
      if (currentTypeOfPosition) {
        const isValid = options.some(
          (option) =>
            String(option.value).toLowerCase() === String(currentTypeOfPosition).toLowerCase()
        );

        if (!isValid) {
          form.setFieldValue('typeOfPosition', undefined);
        }
      }
    };

    if (allEstablishments.length > 0) {
      updateTypeOptions();
    } else {
      setTypeOfPositionOptions([]);
    }
  }, [departmentVal, municipalityVal, allEstablishments, allLocations, form, t]);

  // Filter establishments when typeOfPosition or municipality changes
  useEffect(() => {
    const updateEstablishments = () => {
      // Query the live value from the form store to protect against undefined from useWatch
      const actualTypeOfPositionVal = form.getFieldValue('typeOfPosition');
      const actualMunicipalityVal = form.getFieldValue('municipality');
      const actualDepartmentVal = form.getFieldValue('department');

      if (!actualTypeOfPositionVal && !actualMunicipalityVal) {
        setEstablishmentOptions([]);
        form.setFieldValue('establishment', undefined);
        return;
      }

      // Find the locationId based on municipality
      let locationId: number | undefined = undefined;
      if (actualMunicipalityVal) {
        const matchingLoc = allLocations.find(
          (loc) =>
            String(loc.city).toLowerCase() === String(actualMunicipalityVal).toLowerCase() &&
            (!actualDepartmentVal ||
              String(loc.district).toLowerCase() === String(actualDepartmentVal).toLowerCase())
        );
        if (matchingLoc) {
          locationId = matchingLoc.id;
        }
      }

      const filtered = allEstablishments.filter((est: any) => {
        const location = allLocations.find((loc) => Number(loc.id) === Number(est.locationId));
        const estDept = location?.district || est.department || est.location?.district;
        const estMun = location?.city || est.municipality || est.location?.city;

        let matches = true;

        if (actualDepartmentVal) {
          matches =
            matches && String(estDept).toLowerCase() === String(actualDepartmentVal).toLowerCase();
        }

        if (actualMunicipalityVal) {
          if (locationId !== undefined) {
            matches = matches && Number(est.locationId) === Number(locationId);
          } else {
            matches =
              matches &&
              String(estMun).toLowerCase() === String(actualMunicipalityVal).toLowerCase();
          }
        }

        if (actualTypeOfPositionVal) {
          const estabTypeKey =
            est.establishmentType?.key ||
            est.establishmentType?.nameEn ||
            est.establishmentType ||
            est.positionType;
          matches =
            matches &&
            String(estabTypeKey).toLowerCase() === String(actualTypeOfPositionVal).toLowerCase();
        }

        return matches;
      });

      const options = filtered.map((est) => ({
        label: est.name,
        value: est.name,
        id: est.id,
      }));

      setEstablishmentOptions(options);

      const currentEstablishment = form.getFieldValue('establishment');
      if (currentEstablishment) {
        const isValid = options.some(
          (est) => String(est.value).toLowerCase() === String(currentEstablishment).toLowerCase()
        );
        if (!isValid) {
          form.setFieldValue('establishment', undefined);
        }
      }
    };

    if (allEstablishments.length > 0) {
      updateEstablishments();
    } else {
      setEstablishmentOptions([]);
    }
  }, [typeOfPositionVal, municipalityVal, departmentVal, allEstablishments, form, allLocations]);

  // Show / hide establishment text field for "other" selection
  useEffect(() => {
    const translateOther = t('price:other') || t('common:other') || 'other';
    const isOther =
      establishmentVal &&
      (String(establishmentVal).toLowerCase() === 'other' ||
        String(establishmentVal).toLowerCase() === translateOther.toLowerCase());

    setShowEstablishmentTextField(!!isOther);
  }, [establishmentVal, t]);

  const isLocationsOfflineAndEmpty = useMemo(() => {
    return !isOnline && !loadingLocations && departmentsOptions.length === 0;
  }, [isOnline, loadingLocations, departmentsOptions]);

  return {
    isOnline,
    loadingLocations,
    loadingEstablishments,
    departmentsOptions,
    municipalityOptions,
    typeOfPositionOptions,
    establishmentOptions,
    showEstablishmentTextField,
    continueButtonClicked,
    setContinueButtonClicked,
    isLocationsOfflineAndEmpty,
  };
}
